/**
 * packages/auth/src/utils/authStatus.ts
 *
 * Standard auth status helper — used across all apps to normalize
 * authentication state, email verification, and provider detection.
 */

import type { User } from '../types';

export interface AuthStatusResult {
  /** User is authenticated (has a valid session) */
  isAuthenticated: boolean;
  /** Email is verified (OAuth users always true; email users need email_confirmed_at) */
  isEmailVerified: boolean;
  /** The auth provider used (google, email, etc.) */
  provider: string | null;
  /** User signed up with email/password but hasn't verified yet */
  needsEmailVerification: boolean;
  /** The user's email address */
  email: string | null;
}

/**
 * Determines the auth provider from a Supabase user object.
 * Returns 'google', 'email', etc.
 */
function getProvider(user: User | null): string | null {
  if (!user) return null;
  // Supabase stores provider in app_metadata.provider
  const provider = user.app_metadata?.provider;
  if (typeof provider === 'string') return provider;
  // Fallback: check identities array
  const identities = user.identities;
  if (Array.isArray(identities) && identities.length > 0) {
    return identities[0].provider ?? 'email';
  }
  return 'email';
}

/**
 * Check if an email is verified based on provider and user metadata.
 *
 * - OAuth users (google, github, etc.): always treated as verified by provider
 * - Email/password users: require email_confirmed_at to be set
 */
function checkEmailVerified(user: User | null, provider: string | null): boolean {
  if (!user) return false;
  // OAuth providers are trusted — email is verified by the provider
  if (provider && provider !== 'email') return true;
  // For email/password users, check email_confirmed_at
  // NOTE: magiclink/OTP users will have email_confirmed_at set when they click the link
  const confirmedAt = user.email_confirmed_at;
  return !!confirmedAt;
}

/**
 * Get normalized auth status from a Supabase user object.
 *
 * Usage:
 *   const status = getAuthStatus(authUser);
 *   if (status.needsEmailVerification) showVerifyScreen();
 */
export function getAuthStatus(user: User | null): AuthStatusResult {
  const isAuthenticated = !!user;
  const provider = getProvider(user);
  const isEmailVerified = checkEmailVerified(user, provider);
  const needsEmailVerification = isAuthenticated && provider === 'email' && !isEmailVerified;

  return {
    isAuthenticated,
    isEmailVerified,
    provider,
    needsEmailVerification,
    email: user?.email ?? null,
  };
}

/**
 * Extract the verified email domain from a user.
 * Used for handle verification readiness (reserved-handle email domain checks).
 *
 * - OAuth (google): domain is trusted (extracted from email)
 * - Email/password: domain only trusted if email_confirmed_at is set
 * - Otherwise: null
 */
export function getVerifiedEmailDomain(user: User | null): string | null {
  if (!user?.email) return null;
  const provider = getProvider(user);
  
  if (provider && provider !== 'email') {
    // OAuth: email is provider-verified
    return user.email.split('@')[1] ?? null;
  }
  
  // Email/password: only trust if confirmed
  if (user.email_confirmed_at) {
    return user.email.split('@')[1] ?? null;
  }
  
  return null;
}
